import { ActionType } from "@seneca/session-service-consts";

import logError from "seneca-common/utils/sentry/logError";

import WebsocketConnectionError from "./errors/ConnectionError";
import WebsocketConnectionTimeoutError from "./errors/ConnectionTimeoutError";
import { ClientMessageData } from "./session/action-types";
import constructWsUrlWithAuth from "./utils/constructWsUrlWithAuth";
import isWebSocketSupported from "./utils/isWebSocketSupported";

type SessionId = string;
type SendMessage = (sessionId: SessionId, message: WebSocketMessage) => boolean;

export type CreateConnectionOptions = {
  userId: string;
  sessionId: string;
  timeoutMs?: number;
  eventHandlers?: EventHandlers;
};

export type EventHandlers = {
  onOpen?: (event: Event, sendMessage: SendMessage) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (event: MessageEvent, sendMessage: SendMessage) => void;
};

export type WebSocketMessage = {
  action: ActionType;
  data?: ClientMessageData;
};

class WebSocketManager {
  private static instance: WebSocketManager;
  private sockets: Map<SessionId, WebSocket> = new Map();
  private abortControllers: Map<SessionId, AbortController> = new Map();

  private CONNECTION_OPEN_TIMEOUT_MS = 10_000;
  private CONNECTION_POLLING = {
    INTERVAL_MS: 120_000, // 2 minutes
    MAX_COUNT: 30 // 1 hour
  };

  private constructor() {}

  static getInstance(): WebSocketManager {
    if (!this.instance) {
      this.instance = new WebSocketManager();
    }
    return this.instance;
  }

  private getSocket(sessionId: SessionId) {
    return this.sockets.get(sessionId);
  }

  private setSocket(sessionId: SessionId, socket: WebSocket) {
    this.closeConnection(sessionId);
    this.sockets.set(sessionId, socket);
    this.abortControllers.set(sessionId, new AbortController());
  }

  private isOpenOrConnecting(sessionId: SessionId) {
    const socket = this.getSocket(sessionId);
    if (!socket) return false;
    return (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    );
  }

  private isClosed(sessionId: SessionId) {
    const socket = this.getSocket(sessionId);
    if (!socket) return true;
    return socket.readyState === WebSocket.CLOSED;
  }

  private attachEventHandlers(
    sessionId: SessionId,
    eventHandlers: EventHandlers
  ) {
    const socket = this.getSocket(sessionId);
    if (!socket) return;

    const { onOpen, onClose, onError, onMessage } = eventHandlers;
    const options = { signal: this.abortControllers.get(sessionId)?.signal };

    onOpen &&
      socket.addEventListener(
        "open",
        event => onOpen(event, this.sendMessage.bind(this)),
        options
      );
    onMessage &&
      socket.addEventListener(
        "message",
        event => onMessage(event, this.sendMessage.bind(this)),
        options
      );
    onClose && socket.addEventListener("close", onClose, options);
    onError && socket.addEventListener("error", onError, options);
  }

  private keepConnectionAlive(sessionId: SessionId) {
    let pollingCount = 0;
    const interval = setInterval(() => {
      const isConnectedOver1h =
        pollingCount >= this.CONNECTION_POLLING.MAX_COUNT;

      if (isConnectedOver1h || this.isClosed(sessionId)) {
        clearInterval(interval);
        return;
      }

      const sent = this.sendMessage(sessionId, {
        action: ActionType.KEEP_CONNECTION_ALIVE
      });
      if (sent) pollingCount++;
    }, this.CONNECTION_POLLING.INTERVAL_MS);
  }

  sendMessage(sessionId: SessionId, message: WebSocketMessage) {
    try {
      const socket = this.getSocket(sessionId);
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(message));
        return true;
      }
    } catch (error) {
      logError(error, {
        message: "Websocket send message error",
        extraInfo: message
      });
    }
    return false;
  }

  closeConnection(sessionId: string) {
    const socket = this.getSocket(sessionId);
    if (socket) {
      socket.close();
      this.abortControllers.get(sessionId)?.abort();
      this.sockets.delete(sessionId);
    }
  }

  async getOrCreateConnection({
    userId,
    sessionId,
    timeoutMs = this.CONNECTION_OPEN_TIMEOUT_MS,
    eventHandlers
  }: CreateConnectionOptions) {
    try {
      if (!isWebSocketSupported()) return;

      if (this.isOpenOrConnecting(sessionId)) return this.getSocket(sessionId);

      const url = await constructWsUrlWithAuth(sessionId);
      if (!url) return;

      const socket = new WebSocket(url);
      this.setSocket(sessionId, socket);

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.closeConnection(sessionId);
          reject(new WebsocketConnectionTimeoutError());
        }, timeoutMs);
        const handlers: EventHandlers = {
          ...eventHandlers,
          onError: event => {
            clearTimeout(timeout);
            eventHandlers?.onError?.(event);
            this.closeConnection(sessionId);
            reject(new WebsocketConnectionError(event));
          },
          onOpen: (event, sendMessage) => {
            clearTimeout(timeout);
            this.keepConnectionAlive(sessionId);
            eventHandlers?.onOpen?.(event, sendMessage);
            resolve();
          }
        };
        this.attachEventHandlers(sessionId, handlers);
      });

      return this.getSocket(sessionId);
    } catch (error) {
      let message: string = "Error creating websocket connection";

      if (
        error instanceof WebsocketConnectionError ||
        error instanceof WebsocketConnectionTimeoutError
      ) {
        message = error.message;
      }

      logError(error, {
        message,
        extraInfo: {
          userId,
          sessionId
        }
      });
      return undefined;
    }
  }
}

export default WebSocketManager.getInstance();
